import CheckIcon from '@mui/icons-material/Check';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Link as MuiLink,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FEED_TYPES, type AlertFeedItem, useAlertFeedQuery } from '../api/alert.js';
import { useAlertsLastSeen, useAlertsRead } from '../hooks/useAlertsRead.js';

const TYPE_LABEL: Record<string, string> = {
  'anti-debug': 'Anti-Debug',
  'custom-rule': 'Custom Rule',
  'dependency-change': 'Dependency Change',
  'obfuscated-code': 'Obfuscation'
};

const TYPE_COLOR: Record<string, string> = {
  'anti-debug': '#b71c1c',
  'custom-rule': '#1a237e',
  'obfuscated-code': '#e65100',
  'dependency-change': '#2e7d32'
};

const SEVERITY_COLOR: Record<string, string> = {
  high: '#c62828',
  medium: '#e65100',
  low: '#1565c0',
  info: '#546e7a'
};

const HOURS_OPTIONS = [
  { label: '24h', value: 24 },
  { label: '48h', value: 48 },
  { label: '7d', value: 168 }
];

const SEVERITY_OPTIONS = Object.keys(SEVERITY_COLOR);

function Count({ n }: { n: number }) {
  return (
    <Typography component="span" variant="caption" sx={{ ml: 0.5, opacity: 0.6, fontWeight: 400 }}>
      {n}
    </Typography>
  );
}

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

function detailFor(item: AlertFeedItem): { label?: string; url?: string } {
  const url = item.requestUrl ?? undefined;
  if (item.type === 'custom-rule') return { label: item.name ?? undefined, url };
  return { url };
}

function AlertRow({ item, onDismiss }: { item: AlertFeedItem; onDismiss: (id: string) => void }) {
  const analysisUrl = `/projects/${item.projectId}/analyses/web/${item.analysisId}`;
  const detail = detailFor(item);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '180px 1fr auto 28px 28px',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 0.6,
        '&:hover': { bgcolor: 'action.hover' }
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
        <Chip
          label={item.severity}
          size="small"
          sx={{
            height: 14,
            fontSize: '0.6rem',
            bgcolor: SEVERITY_COLOR[item.severity] ?? '#546e7a',
            color: '#fff',
            flexShrink: 0,
            '& .MuiChip-label': { px: 0.6 }
          }}
        />
        <Typography
          variant="caption"
          fontWeight={700}
          noWrap
          sx={{ color: TYPE_COLOR[item.type] ?? 'text.primary', fontFamily: 'monospace' }}
        >
          {TYPE_LABEL[item.type] ?? item.type}
        </Typography>
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'baseline',
            gap: 1,
            minWidth: 0,
            overflow: 'hidden'
          }}
        >
          {detail.label && (
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ fontFamily: 'monospace', flexShrink: 0 }}
              title={detail.label}
            >
              {detail.label}
            </Typography>
          )}
          {detail.url && (
            <MuiLink
              href={detail.url}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              variant="caption"
              color="text.secondary"
              onClick={(e) => e.stopPropagation()}
              sx={{
                fontFamily: 'monospace',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0
              }}
              title={detail.url}
            >
              {detail.url}
            </MuiLink>
          )}
        </Box>
      </Box>

      <Tooltip title={new Date(item.createdDate).toLocaleString()}>
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ whiteSpace: 'nowrap', pt: 0.1, cursor: 'default' }}
        >
          {timeAgo(item.createdDate)}
        </Typography>
      </Tooltip>

      <Tooltip title="Open analysis">
        <IconButton size="small" component={Link} to={analysisUrl} sx={{ p: 0.25 }}>
          <OpenInNewIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>

      <Tooltip title="Dismiss">
        <IconButton size="small" onClick={() => onDismiss(item.id)} sx={{ p: 0.25 }}>
          <CheckIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

interface ProjectGroup {
  projectId: string;
  projectName: string;
  items: AlertFeedItem[];
}

const TYPE_ORDER: Record<string, number> = Object.fromEntries(FEED_TYPES.map((t, i) => [t, i]));

function groupByProject(items: AlertFeedItem[]): ProjectGroup[] {
  const map = new Map<string, ProjectGroup>();
  for (const item of items) {
    let group = map.get(item.projectId);
    if (!group) {
      group = { projectId: item.projectId, projectName: item.projectName, items: [] };
      map.set(item.projectId, group);
    }
    group.items.push(item);
  }
  for (const group of map.values()) {
    group.items.sort((a, b) => {
      const typeDelta = (TYPE_ORDER[a.type] ?? 99) - (TYPE_ORDER[b.type] ?? 99);
      if (typeDelta !== 0) return typeDelta;
      return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
    });
  }
  return [...map.values()].sort(
    (a, b) => new Date(b.items[0]!.createdDate).getTime() - new Date(a.items[0]!.createdDate).getTime()
  );
}

function ProjectGroup({ group, onDismiss }: { group: ProjectGroup; onDismiss: (ids: string[]) => void }) {
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.6,
          bgcolor: 'action.selected',
          borderRadius: '4px 4px 0 0'
        }}
      >
        <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
          {group.projectName}
        </Typography>
        <Chip label={group.items.length} size="small" sx={{ height: 18, fontSize: '0.7rem' }} />
        <Tooltip title="Dismiss all in group">
          <IconButton size="small" onClick={() => onDismiss(group.items.map((a) => a.id))} sx={{ p: 0.25 }}>
            <DoneAllIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {group.items.map((item, i) => (
        <Box key={item.id}>
          <AlertRow item={item} onDismiss={(id) => onDismiss([id])} />
          {i < group.items.length - 1 && <Divider />}
        </Box>
      ))}
    </Box>
  );
}

export function AlertsFeedView() {
  const [typeFilter, setTypeFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('high');
  const [showDismissed, setShowDismissed] = useState(false);
  const [hours, setHours] = useState(24);

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useAlertFeedQuery({
    hours,
    severity: severityFilter || undefined,
    types: FEED_TYPES.join(',')
  });
  const { dismissed, dismiss } = useAlertsRead();
  const { markSeen } = useAlertsLastSeen();

  useEffect(() => {
    markSeen();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const baseItems = useMemo(() => {
    const all = data?.pages.flatMap((p) => p.items) ?? [];
    return showDismissed ? all : all.filter((a) => !dismissed.has(a.id));
  }, [data, dismissed, showDismissed]);

  const typeCounts = useMemo(() => {
    const tc: Record<string, number> = {};
    for (const a of baseItems) {
      tc[a.type] = (tc[a.type] ?? 0) + 1;
    }
    return tc;
  }, [baseItems]);

  const visible = useMemo(() => {
    if (!typeFilter) return baseItems;
    return baseItems.filter((a) => a.type === typeFilter);
  }, [baseItems, typeFilter]);

  const groups = useMemo(() => groupByProject(visible), [visible]);

  const unreadCount = showDismissed ? 0 : baseItems.length;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography color="error">{error.message}</Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>
          Alerts
        </Typography>
        {unreadCount > 0 && <Chip label={`${unreadCount} new`} color="error" size="small" />}
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Dismiss all visible">
          <span>
            <IconButton
              size="small"
              disabled={visible.length === 0}
              onClick={() => dismiss(visible.map((a) => a.id))}
            >
              <DoneAllIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <ToggleButtonGroup
          value={typeFilter}
          exclusive
          onChange={(_, v) => setTypeFilter(v ?? '')}
          size="small"
        >
          <ToggleButton value="">
            All <Count n={baseItems.length} />
          </ToggleButton>
          {FEED_TYPES.map((t) => (
            <ToggleButton key={t} value={t}>
              {TYPE_LABEL[t] ?? t} <Count n={typeCounts[t] ?? 0} />
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <ToggleButtonGroup
          value={severityFilter}
          exclusive
          onChange={(_, v) => setSeverityFilter(v ?? '')}
          size="small"
        >
          <ToggleButton value="">Any</ToggleButton>
          {SEVERITY_OPTIONS.map((s) => (
            <ToggleButton key={s} value={s} sx={{ textTransform: 'capitalize' }}>
              {s}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <ToggleButtonGroup
          value={hours}
          exclusive
          onChange={(_, v) => {
            if (v !== null) setHours(v);
          }}
          size="small"
        >
          {HOURS_OPTIONS.map((o) => (
            <ToggleButton key={o.value} value={o.value}>
              {o.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <ToggleButton
          value="dismissed"
          selected={showDismissed}
          onChange={() => setShowDismissed((v) => !v)}
          size="small"
        >
          Show dismissed
        </ToggleButton>
      </Box>

      {groups.length === 0 ? (
        <Typography color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
          {showDismissed ? `No alerts in the last ${hours}h.` : 'All caught up — nothing new to review.'}
        </Typography>
      ) : (
        groups.map((group) => <ProjectGroup key={group.projectId} group={group} onDismiss={dismiss} />)
      )}

      {hasNextPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, mb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            startIcon={isFetchingNextPage ? <CircularProgress size={12} /> : undefined}
          >
            {isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </Box>
      )}
    </>
  );
}
